import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { OfficeMap } from "@/components/site/OfficeMap";
import { MapPin, Car, Bus, Clock, TreePine, Navigation } from "lucide-react";
import heroImg from "@/assets/intro-forest.jpg";
import siteConfig from "@/config/site.json";
import { useT } from "@/lib/i18n";

export const Route = createFileRoute("/about/oficinas")({
  component: Locations,
  head: () => ({
    meta: [
      { title: "Dónde estamos — Bosque Suspendido (Sevilla)" },
      { name: "description", content: "Cómo llegar a Bosque Suspendido: Centro de Naturaleza La Juliana, Bollullos de la Mitación, a 15 minutos de Sevilla. Parking gratuito y acceso para autobuses." },
      { property: "og:title", content: "Dónde estamos — Bosque Suspendido" },
      { property: "og:description", content: "En el pinar de La Juliana, a 15 minutos de Sevilla. Cómo llegar, parking y contacto." },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/about/oficinas" },
      { property: "og:image", content: heroImg },
    ],
    links: [{ rel: "canonical", href: "https://bosque-suspendido.lovable.app/about/oficinas" }],
  }),
});

function Locations() {
  const { t } = useT();

  const facts = [
    {
      icon: MapPin,
      label: t("Dirección", "Morada", "Address"),
      value: t(
        "Ctra. A-474 Bollullos de la Mitación — Aznalcázar, Km 15,5 (Sevilla)",
        "Ctra. A-474 Bollullos de la Mitación — Aznalcázar, Km 15,5 (Sevilha)",
        "A-474 road Bollullos de la Mitación — Aznalcázar, Km 15.5 (Seville)",
      ),
    },
    {
      icon: Clock,
      label: t("A 15 minutos", "A 15 minutos", "15 minutes away"),
      value: t(
        "Desde Sevilla capital por la A-49. Escápate al bosque sin salir casi de la ciudad.",
        "Desde Sevilha pela A-49. Foge para o bosque quase sem sair da cidade.",
        "From Seville city via the A-49. Escape to the forest without really leaving town.",
      ),
    },
    {
      icon: Car,
      label: t("Parking gratuito", "Estacionamento grátis", "Free parking"),
      value: t(
        "Aparcamiento gratuito junto a la entrada, para coches y autobuses.",
        "Estacionamento gratuito junto à entrada, para carros e autocarros.",
        "Free parking next to the entrance, for cars and coaches.",
      ),
    },
    {
      icon: TreePine,
      label: t("En plena naturaleza", "Em plena natureza", "In the heart of nature"),
      value: t(
        "Centro de Naturaleza La Juliana: un pinar centenario de más de 25 hectáreas.",
        "Centro de Natureza La Juliana: um pinhal centenário com mais de 25 hectares.",
        "La Juliana Nature Centre: a century-old pine forest of over 25 hectares.",
      ),
    },
  ];

  const steps = [
    {
      icon: Car,
      title: t("En coche desde Sevilla", "De carro desde Sevilha", "By car from Seville"),
      body: t(
        "Toma la A-49 dirección Huelva y sal en Bollullos de la Mitación. Sigue la A-474 dirección Aznalcázar hasta el km 15,5: la entrada al Centro de Naturaleza La Juliana está señalizada.",
        "Apanha a A-49 em direção a Huelva e sai em Bollullos de la Mitación. Segue pela A-474 em direção a Aznalcázar até ao km 15,5: a entrada do Centro de Natureza La Juliana está sinalizada.",
        "Take the A-49 towards Huelva and exit at Bollullos de la Mitación. Follow the A-474 towards Aznalcázar until km 15.5: the entrance to La Juliana Nature Centre is signposted.",
      ),
    },
    {
      icon: Bus,
      title: t("Grupos y autobuses", "Grupos e autocarros", "Groups & coaches"),
      body: t(
        "¿Vienes con el cole, un campamento o una empresa? El acceso y el aparcamiento están preparados para autobuses. Escríbenos y te ayudamos a organizar la llegada.",
        "Vens com a escola, um acampamento ou uma empresa? O acesso e o estacionamento estão preparados para autocarros. Escreve-nos e ajudamos-te a organizar a chegada.",
        "Coming with a school, camp or company? Access and parking are coach-ready. Write to us and we'll help you plan the arrival.",
      ),
    },
    {
      icon: Navigation,
      title: t("Con tu GPS", "Com o teu GPS", "With your GPS"),
      body: t(
        "Busca «Bosque Suspendido» o «Centro de Naturaleza La Juliana» en Google Maps y te llevará directo a la puerta del parque.",
        "Procura «Bosque Suspendido» ou «Centro de Naturaleza La Juliana» no Google Maps e chegas direto à porta do parque.",
        "Search for “Bosque Suspendido” or “Centro de Naturaleza La Juliana” on Google Maps and it will take you straight to the park gate.",
      ),
    },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="relative border-b border-gold/15 overflow-hidden">
        <img
          src={heroImg}
          alt={t(
            "Pinar del Centro de Naturaleza La Juliana, hogar de Bosque Suspendido",
            "Pinhal do Centro de Natureza La Juliana, casa do Bosque Suspendido",
            "La Juliana pine forest, home of Bosque Suspendido",
          )}
          width={1920}
          height={1080}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 pt-44 pb-20 md:pt-52 md:pb-28">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {t("Dónde estamos", "Onde estamos", "Where we are")}
          </span>
          <h1 className="font-serif text-5xl md:text-7xl mt-5 max-w-3xl">
            {t("Un bosque a ", "Um bosque a ", "A forest just ")}
            <em className="not-italic gold-gradient">
              {t("15 minutos de Sevilla", "15 minutos de Sevilha", "15 minutes from Seville")}
            </em>
          </h1>
          <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed">
            {t(
              "Bosque Suspendido vive en el pinar del Centro de Naturaleza La Juliana, en Bollullos de la Mitación. Aquí tienes todo lo que necesitas para llegar sin perderte.",
              "O Bosque Suspendido vive no pinhal do Centro de Natureza La Juliana, em Bollullos de la Mitación. Aqui tens tudo o que precisas para chegar sem te perderes.",
              "Bosque Suspendido lives in the La Juliana pine forest, in Bollullos de la Mitación. Here is everything you need to get here without getting lost.",
            )}
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <a
              href="https://www.google.com/maps/search/?api=1&query=Centro+de+Naturaleza+La+Juliana+Bollullos+de+la+Mitaci%C3%B3n"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 wood-btn px-7 py-3.5 text-xs tracking-[0.3em] uppercase"
            >
              <Navigation size={14} />
              {t("Abrir en Google Maps", "Abrir no Google Maps", "Open in Google Maps")}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 wood-btn px-7 py-3.5 text-xs tracking-[0.3em] uppercase"
            >
              {t("Reserva tu aventura", "Reserva a tua aventura", "Book your adventure")}
            </Link>
          </div>
        </div>
      </section>

      {/* Quick facts */}
      <section className="border-b border-gold/15 bg-card/40">
        <div className="max-w-7xl mx-auto px-6 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {facts.map((f) => (
            <div key={f.label} className="rounded-2xl border border-gold/20 bg-background/40 p-6">
              <f.icon size={22} className="text-gold" />
              <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-gold/80">{f.label}</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">{f.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How to arrive */}
      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {t("Cómo llegar", "Como chegar", "Getting here")}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl mt-4">
              {t("Tres formas de ", "Três formas de ", "Three ways to ")}
              <em className="not-italic gold-gradient">
                {t("plantarte en el pinar", "chegar ao pinhal", "reach the pines")}
              </em>
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={s.title} className="relative rounded-2xl border border-gold/20 bg-card/30 p-7">
                <span className="absolute top-5 right-6 font-serif text-4xl text-gold/15">{i + 1}</span>
                <s.icon size={24} className="text-gold" />
                <h3 className="mt-4 font-serif text-xl">{s.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-muted-foreground">
            {t(
              "¿Dudas sobre horarios o disponibilidad? Llámanos al ",
              "Dúvidas sobre horários ou disponibilidade? Liga-nos para ",
              "Questions about opening times or availability? Call us on ",
            )}
            <a href={`tel:${siteConfig.contact.phones[0].number.replace(/\s/g, "")}`} className="text-gold hover:underline">
              {siteConfig.contact.phones[0].number}
            </a>
            {t(" o escríbenos por WhatsApp.", " ou escreve-nos no WhatsApp.", " or message us on WhatsApp.")}
          </p>
        </div>
      </section>

      {/* Map + contact cards */}
      <OfficeMap />
    </Layout>
  );
}
